import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { POST } from '../services/api';
import { 
  Eye, EyeOff, Lock, Shield, Check, Info, 
  AlertTriangle, Loader2, X 
} from 'lucide-react';

const ChangePassword = () => {
  const navigate = useNavigate();

  // --- 1. State for Data ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // --- 2. State for UI Logic ---
  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- 3. Helpers (Toast Timer) ---
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // --- 4. Validation Logic ---
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Checks
    if (!currentPassword) newErrors.currentPassword = 'Current password is required';
    
    // Kept only length check
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!confirmNewPassword) {
      newErrors.confirmNewPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmNewPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match';
    }

    setErrors(newErrors);

    // If Valid, Show Confirmation
    if (Object.keys(newErrors).length === 0) {
      setConfirmationDialog({
        header: 'Change Password',
        message: 'Are you sure you want to change your admin password? You will need to use the new password for future logins.',
        onConfirm: executePasswordChange,
        onCancel: () => setConfirmationDialog(null)
      });
    }
  };

  // --- 5. API Execution ---
  const executePasswordChange = async () => {
    setLoading(true);
    setConfirmationDialog(null); // Close dialog immediately

    try {
      const payload = {
        currentPassword: currentPassword,
        newPassword: newPassword,
        confirmPassword: confirmNewPassword // API requires this key
      };

      const response = await POST('/change-admin-password', payload);

      if (response && response.success) {
        showToast(response.message || 'Password changed successfully!', 'success');
        
        // Reset Form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        
        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate('/admin/dashboard'); 
        }, 1500);

      } else {
        showToast(response?.message || 'Failed to change password.', 'error');
      }
    } catch (error) {
      console.error("Password Change Error:", error);
      showToast(error.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setErrors({});
    navigate(-1); // Go back to previous page
  };

  const toggleVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // --- Reusable Input Class based on your global theme ---
  const inputClass = (hasError) => `w-full p-3.5 bg-slate-50 border rounded-xl focus:outline-none focus:ring-2 transition-all font-medium text-slate-700 text-sm pl-4 pr-12
    ${hasError ? 'border-red-400 focus:ring-red-200 bg-red-50/30' : 'border-slate-200 focus:ring-blue-500 focus:border-transparent'}`;

  const labelClass = "block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 ml-1";

  return (
    // Fixed layout logic: Centered vertically and horizontally without excessive top margin that causes scrolling
    <div className="flex min-h-[70vh] items-center justify-center">
      
      <style>{`
        .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* --- Toast Notification --- */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl text-white font-bold flex items-center gap-3 z-[1000] animate-in slide-in-from-bottom-5
          ${toast.type === 'success' ? 'bg-emerald-600' : toast.type === 'error' ? 'bg-red-500' : 'bg-blue-600'}`}
        >
          {toast.type === 'success' ? <Check className="w-5 h-5" /> : <Info className="w-5 h-5" />}
          {toast.message}
          <button onClick={() => setToast(null)}><X className="w-4 h-4 ml-2" /></button>
        </div>
      )}

      {/* --- Confirmation Dialog --- */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in">
          <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full text-center border border-slate-100 scale-100 animate-in zoom-in-95">
            <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-amber-100">
                <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">{confirmationDialog.header}</h3>
            <p className="text-slate-500 mb-6 font-medium">{confirmationDialog.message}</p>
            <div className="flex gap-3">
                <button onClick={confirmationDialog.onCancel} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
                <button onClick={confirmationDialog.onConfirm} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold transition-colors shadow-lg shadow-blue-200">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* --- Main Form Card --- */}
      <div className="bg-white w-full max-w-xl rounded-3xl shadow-sm border border-slate-200 p-8 animate-fade-in-up">
        
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-100">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shadow-inner">
                <Shield className="w-6 h-6" />
            </div>
            <div>
                <h2 className="text-2xl font-bold text-slate-800">Security Settings</h2>
                <p className="text-slate-500 mt-1 text-sm font-medium">Update your admin password to stay secure.</p>
            </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          
          {/* 1. Current Password */}
          <div>
            <label className={labelClass}>Current Password *</label>
            <div className="relative">
              <input
                type={showPassword.current ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => {
                   setCurrentPassword(e.target.value);
                   if(errors.currentPassword) setErrors({...errors, currentPassword: ''});
                }}
                className={inputClass(errors.currentPassword)}
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => toggleVisibility('current')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showPassword.current ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="mt-1.5 text-xs text-red-500 font-bold flex items-center">
                 {errors.currentPassword}
              </p>
            )}
          </div>

          {/* 2. New Password */}
          <div>
            <label className={labelClass}>New Password *</label>
            <div className="relative">
              <input
                type={showPassword.new ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if(errors.newPassword) setErrors({...errors, newPassword: ''});
                }}
                className={inputClass(errors.newPassword)}
                placeholder="Create new password"
              />
              <button
                type="button"
                onClick={() => toggleVisibility('new')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showPassword.new ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            {errors.newPassword ? (
              <p className="mt-1.5 text-xs text-red-500 font-bold flex items-center">
                 {errors.newPassword}
              </p>
            ) : (
               <p className="mt-2 text-xs text-slate-400 font-medium">
                 Must be at least 6 characters.
               </p>
            )}
          </div>

          {/* 3. Confirm Password */}
          <div>
            <label className={labelClass}>Confirm New Password *</label>
            <div className="relative">
              <input
                type={showPassword.confirm ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => {
                  setConfirmNewPassword(e.target.value);
                  if(errors.confirmNewPassword) setErrors({...errors, confirmNewPassword: ''});
                }}
                className={inputClass(errors.confirmNewPassword)}
                placeholder="Repeat new password"
              />
              <button
                type="button"
                onClick={() => toggleVisibility('confirm')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showPassword.confirm ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            {errors.confirmNewPassword && (
              <p className="mt-1.5 text-xs text-red-500 font-bold flex items-center">
                 {errors.confirmNewPassword}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 pt-6 mt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="w-1/3 py-3.5 px-4 bg-white border border-slate-200 rounded-xl text-slate-600 font-bold hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-2/3 py-3.5 px-4 bg-blue-600 rounded-xl text-white font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Update Password
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ChangePassword;